// src/components/audio/PracticeAudioPlayer.jsx
// Full-control audio player for practice mode (no play-count restriction, seek + skip + speed)
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Volume2, VolumeX, Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

const RATES = [0.75, 1, 1.25, 1.5, 2];

function fmtTime(s) {
  if (!s || isNaN(s) || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * PracticeAudioPlayer
 * Unlike RestrictedAudioPlayer, this gives full control: seekable progress bar,
 * ±10s skip buttons, speed selector, and no play-count limit.
 * playCount is informational only (shown as badge so learner knows the exam rule).
 */
const PracticeAudioPlayer = forwardRef(({ audioUrl, playCount = 1, className = '', onPlayingStateChange }, ref) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Expose control methods to parent
  useImperativeHandle(ref, () => ({
    play: () => {
      const audio = audioRef.current;
      if (audio) audio.play().catch(console.error);
    },
    pause: () => {
      const audio = audioRef.current;
      if (audio) audio.pause();
    },
    togglePlay: () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (isPlaying) audio.pause();
      else audio.play().catch(console.error);
    },
    reset: () => {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.pause();
      }
    },
    isPlaying: () => isPlaying,
  }), [isPlaying]);

  // Re-init when audioUrl changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    setIsLoading(true);
    setError(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    const onLoadStart = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);
    const onLoadedMetadata = () => setDuration(audio.duration || 0);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onError = () => { setError('Audio konnte nicht geladen werden'); setIsLoading(false); };

    audio.addEventListener('loadstart', onLoadStart);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    audio.volume = isMuted ? 0 : volume;
    audio.playbackRate = rate;

    return () => {
      audio.removeEventListener('loadstart', onLoadStart);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  useEffect(() => {
    if (onPlayingStateChange) onPlayingStateChange(isPlaying);
  }, [isPlaying, onPlayingStateChange]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.pause();
    else audio.play().catch(console.error);
  };

  const skip = (sec) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + sec));
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = parseFloat(e.target.value);
  };

  // No audio state
  if (!audioUrl) {
    return (
      <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-dashed border-gray-300 bg-gray-50 ${className}`}>
        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
          <Volume2 className="w-3.5 h-3.5 text-gray-400" />
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500">Audio nicht verfügbar</p>
          <p className="text-[10px] text-gray-400">Übe die Aufgaben ohne Audio</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`px-3.5 py-2.5 rounded-xl border border-red-200 bg-red-50 text-xs text-red-600 ${className}`}>
        {error}
      </div>
    );
  }

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`p-3.5 rounded-xl border border-[#3A86FF]/20 bg-gradient-to-br from-[#EEF5FF] to-white ${className}`}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" style={{ display: 'none' }} />

      {/* Exam badge + practice note */}
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-[10px] font-bold uppercase tracking-wide text-[#275eb4] bg-[#3A86FF]/10 border border-[#3A86FF]/15 px-2 py-0.5 rounded-full">
          Prüfung: {playCount}× abspielen
        </span>
        <span className="text-[10px] text-gray-400 font-medium">· Üben: unbegrenzt</span>
      </div>

      {/* Seek bar */}
      <div className="mb-2.5">
        <input
          type="range"
          min="0"
          max={duration || 100}
          step="0.1"
          value={currentTime}
          onChange={handleSeek}
          disabled={isLoading || !duration}
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer disabled:cursor-default"
          style={{
            background: `linear-gradient(to right, #3A86FF 0%, #3A86FF ${pct}%, #CBD5E1 ${pct}%, #CBD5E1 100%)`
          }}
          aria-label="Audiowiedergabe"
        />
        <div className="flex justify-between mt-0.5">
          <span className="text-[10px] text-gray-500 font-mono tabular-nums">{fmtTime(currentTime)}</span>
          <span className="text-[10px] text-gray-500 font-mono tabular-nums">{fmtTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-1.5">
        {/* Skip back 10s */}
        <button
          onClick={() => skip(-10)}
          disabled={isLoading}
          className="min-h-[36px] min-w-[36px] inline-flex items-center justify-center rounded-lg text-[#275eb4] hover:bg-[#3A86FF]/10 disabled:opacity-40 touch-manipulation transition-colors"
          title="−10 Sekunden"
          aria-label="10 Sekunden zurück"
        >
          <SkipBack className="w-4 h-4" />
        </button>

        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          disabled={isLoading}
          className="min-h-[40px] min-w-[40px] inline-flex items-center justify-center rounded-full bg-[#3A86FF] text-white disabled:opacity-40 hover:bg-[#275eb4] active:scale-95 touch-manipulation transition-all shadow-[0_4px_12px_rgba(58,134,255,0.3)]"
          aria-label={isPlaying ? 'Pause' : 'Abspielen'}
        >
          {isLoading ? (
            <LogamiLogoSpinner size="sm" decorative={false} alt="Logami loading" />
          ) : isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 translate-x-px" />
          )}
        </button>

        {/* Skip forward 10s */}
        <button
          onClick={() => skip(10)}
          disabled={isLoading}
          className="min-h-[36px] min-w-[36px] inline-flex items-center justify-center rounded-lg text-[#275eb4] hover:bg-[#3A86FF]/10 disabled:opacity-40 touch-manipulation transition-colors"
          title="+10 Sekunden"
          aria-label="10 Sekunden vor"
        >
          <SkipForward className="w-4 h-4" />
        </button>

        <div className="flex-1" />

        {/* Speed selector */}
        <div className="flex items-center gap-0.5">
          {RATES.map(r => (
            <button
              key={r}
              onClick={() => setRate(r)}
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md transition-colors touch-manipulation ${
                rate === r ? 'bg-[#3A86FF] text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
              aria-label={`${r}× Geschwindigkeit`}
            >
              {r}×
            </button>
          ))}
        </div>

        {/* Volume toggle */}
        <button
          onClick={() => setIsMuted(m => !m)}
          className="min-h-[36px] min-w-[36px] inline-flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 touch-manipulation transition-colors"
          aria-label={isMuted ? 'Ton einschalten' : 'Stumm schalten'}
        >
          {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>

        {/* Volume slider */}
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={isMuted ? 0 : volume}
          onChange={e => {
            const v = parseFloat(e.target.value);
            setVolume(v);
            if (v > 0) setIsMuted(false);
          }}
          className="w-14 h-1.5 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #3A86FF 0%, #3A86FF ${(isMuted ? 0 : volume) * 100}%, #CBD5E1 ${(isMuted ? 0 : volume) * 100}%, #CBD5E1 100%)`
          }}
          aria-label="Lautstärke"
        />
      </div>
    </div>
  );
});

export default PracticeAudioPlayer;
